import { useState, useEffect } from "react";
import { LargeContentBox } from "@/components/common/LargeContentBox";
import { PrivateMainLayout } from "@/components/layout/private/main/PrivateMainLayout";
import { Add, HealthAndSafety } from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { Coverage } from "fhir/r5";
import { CoverageModal } from "./components/CoverageModal";
import { InsuranceCard } from "./components/InsuranceCard";
import { useCoverage } from "@/services/fhir/coverage/useCoverage";
import { oids } from "@/configs/Settings";
import { PageHeader } from "@/components/common/PageHeader";
import { PatchCoverage } from "@/services/fhir/coverage/PatchCoverage";

interface InsuranceFormData {
  organization: { value: string; label: string } | null;
  insurancePlan: { value: string; label: string } | null;
  cardNumber: string;
}

export default function PatientInsurance() {
  const { user } = useSessionContext();
  const patientId = `${oids.cpf}-${user.username}`;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCoverage, setSelectedCoverage] = useState<Coverage | null>(
    null
  );

  // Hook para buscar e gerenciar coberturas
  const { coverage, loading, fetchCoverage } = useCoverage();

  // Carregar coberturas ao montar o componente
  useEffect(() => {
    if (patientId) {
      fetchCoverage({ patient: patientId });
    }
  }, [patientId, fetchCoverage]);

  // Handler para abrir modal de edição
  const handleEdit = (coverage: Coverage) => {
    setSelectedCoverage(coverage);
    setIsModalOpen(true);
  };

  // Handler para deletar cobertura
  const handleToggleStatus = async (coverage: Coverage) => {
    try {
      await PatchCoverage({
        id: coverage.id || "",
        status: coverage.status === "active" ? "cancelled" : "active",
        hasProperty: coverage.hasOwnProperty("status"),
      });
      toast.success("Atualizado com sucesso!");
      fetchCoverage({ patient: patientId });
    } catch (error) {
      toast.warning(
        "Não foi possível finalizar a ação, por favor tente novamente."
      );
    }
  };

  // Handler para fechar o modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCoverage(null);
  };

  // Handler para sucesso na operação
  const handleSuccess = () => {
    fetchCoverage({ patient: patientId });
  };

  return (
    <LargeContentBox>
      <Stack sx={{ width: "100%" }}>
        <PageHeader
          title="Planos de Saúde"
          icon={HealthAndSafety}
          description="Gerencie seus planos de saúde"
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setIsModalOpen(true)}
            >
              Nova Carteirinha
            </Button>
          }
        />

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={40} color="primary" sx={{ mb: 2 }} />
            <Typography variant="body1" color="neutral700.main">
              Carregando carteirinhas...
            </Typography>
          </Box>
        ) : coverage.length > 0 ? (
          <Grid container width="100%" spacing={2}>
            {coverage.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={10}
                md={6}
                lg={6}
                key={item.id}
                sx={{
                  ...(index % 2 === 0 &&
                    !isMobile && {
                    paddingLeft: "0px !important",
                  }),
                }}
              >
                <InsuranceCard
                  coverage={item}
                  onEdit={() => handleEdit(item)}
                  onDelete={() => item.id && handleToggleStatus(item)}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              borderRadius: "15px",
              background: "linear-gradient(to bottom, #f9f9f9, #ffffff)",
              border: "1px solid",
              borderColor: "neutral700.main",
            }}
          >
            <HealthAndSafety
              sx={{
                fontSize: 64,
                color: "neutral400.main",
                mb: 2,
                opacity: 0.7,
              }}
            />
            <Typography variant="h6" color="neutral700.main" gutterBottom>
              Nenhuma carteirinha cadastrada
            </Typography>
            <Typography
              variant="body1"
              color="neutral700.main"
              align="center"
              sx={{ maxWidth: 400, mb: 3 }}
            >
              Clique no botão acima para adicionar uma nova carteirinha de plano
              de saúde
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Add />}
              onClick={() => setIsModalOpen(true)}
              sx={{ mt: 1 }}
            >
              Adicionar Carteirinha
            </Button>
          </Box>
        )}
      </Stack>

      {isModalOpen && (
        <CoverageModal
          open={isModalOpen}
          isEditing={!!selectedCoverage}
          patientId={user.username || ""}
          initialData={
            selectedCoverage
              ? {
                organization: {
                  value:
                    selectedCoverage.insurer?.reference?.split("/")[1] || "",
                  label: selectedCoverage.insurer?.display || "",
                },
                insurancePlan: {
                  value:
                    selectedCoverage.insurancePlan?.reference?.split(
                      "/"
                    )[1] || "",
                  label: selectedCoverage.insurancePlan?.display || "",
                  display: selectedCoverage.insurancePlan?.display,
                },
                cardNumber: selectedCoverage.identifier?.[0]?.value || "",
                id: selectedCoverage.id,
              }
              : undefined
          }
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}
    </LargeContentBox>
  );
}
